/** @jsxImportSource @emotion/react */
import {
  Spinner,
  InputGroup,
  Input,
  InputRightElement,
  Button,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaCopy, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  GetDomainInfoResponse,
  MailgunApi,
  RecordType,
  Valid,
} from "../../api/Mailgun";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { TestDriveBannerAtom } from "../../atoms/TestDriveBannerAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import Skeleton from "../Skeleton";
import StyledButton from "../StyledButton";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }

  .section {
    width: 100%;

    &:first-of-type {
      margin-bottom: 32px;
    }
  }

  .header {
    width: 100%;

    padding: 16px 32px;

    border-radius: 4px;

    background: #eaeaea;

    .header-title {
      font-size: 16px;
      font-weight: 700;
      color: #222;
    }
  }

  .grid-row {
    width: 100%;

    display: grid;
    grid-template-columns: 16px 96px minmax(120px, 160px) 240px;
    grid-gap: 32px;
    align-items: center;
    justify-content: space-between;

    @media screen and (max-width: 1140px) {
      grid-gap: 24px;
    }
  }
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  .main-title {
    color: #222222;
    font-weight: 600;
    font-size: 18px;
  }

  .main-text {
    color: #707070;
    font-weight: 400;
    font-size: 15px;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  margin-bottom: 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WrapperList = styled.div`
  width: 100%;

  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
`;

const ItemList = styled.div`
  width: 100%;

  padding: 16px 32px;

  background: #fff;

  border-bottom: 1px solid rgba(0, 0, 0, 0.16);

  .row-flex {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: space-between;

    grid-gap: 16px;
  }

  .row-text {
    font-size: 16px;
    font-weight: 400;
    color: #222;
    word-break: break-all;
  }

  .button {
    display: flex;
    align-items: center;
    justify-content: center;

    background: transparent;

    color: #555555;
    font-weight: 600;

    border-radius: 5px;
    border: 1px solid #dddddd;

    cursor: pointer;
    transition: all 0.3s ease-in-out;

    &:hover {
      opacity: 0.7;
    }
  }

  .icon-button {
    min-width: 40px;
    max-width: 40px;
    height: 40px;

    padding: 8px 0px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 5px;
    border: 1px solid #dddddd;

    cursor: pointer;
    transition: all 0.3s ease-in-out;

    svg {
      color: #555555;
    }

    &:hover {
      svg {
        transition: all 0.3s ease-in-out;
        opacity: 0.7;
      }
    }
  }
`;

export const DNS_ERROR: any = {
  sending_dns_records: [
    {
      record_type: RecordType.Txt,
      valid: Valid.Unknown,
      name: "Error",
      value: "Error",
    },
    {
      record_type: RecordType.Txt,
      valid: Valid.Unknown,
      name: "Error",
      value: "Error",
    },
    {
      record_type: RecordType.Cname,
      valid: Valid.Unknown,
      name: "Error",
      value: "Error",
    },
  ],
};

export default function DNSRecords() {
  const showTestDriveBanner = useRecoilValue(TestDriveBannerAtom);
  const user = useRecoilValue(UserAtom);

  let [domainInfo, setDomainInfo] = useState<GetDomainInfoResponse>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  useEffect(() => {
    handleVerifyClick();
  }, []);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.DNSRecords],
      helpContext: IHelpContexts.DNSRecords,
    });
  }, []);

  async function handleVerifyClick() {
    setIsLoading(true);
    const data = await MailgunApi.getDomainInfo();
    if (data?.sending_dns_records.length !== 3) {
      setDomainInfo(DNS_ERROR);
      toast("Error verifying your DNS", { type: "error" });
    } else {
      setDomainInfo(data);
    }
    setIsLoading(false);
  }

  function LoadingSkeleton() {
    return (
      <>
        {showTestDriveBanner.show ? (
          <Skeleton
            css={css`
              width: 30px;
              height: 20px;
            `}
          />
        ) : (
          <Spinner
            size="sm"
            thickness="2px"
            speed="0.65s"
            emptyColor="gray.200"
            color="orange.500"
          />
        )}

        <Skeleton
          css={css`
            width: 30px;
            height: 20px;
          `}
        />
        <Skeleton
          css={css`
            width: 250px;
            height: 20px;
          `}
        />
        <Skeleton
          css={css`
            width: 250px;
            height: 20px;
          `}
        />
      </>
    );
  }

  return (
    <Container>
      {showTestDriveBanner.show && (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: "#ffa500",
            fontSize: 32,
          }}
        >
          ~ DNS SETTINGS ARE NOT ACTIVE DURING YOUR TEST DRIVE ~
        </div>
      )}

      <div className="section">
        <SpaceBetween>
          <TextWrapper>
            <div className="main-title">Sending records</div>
            {/* <div className="main-text">
              Lorem ipsum dolor sit, amet consectetur adipisicing elit.
              Blanditiis, sapiente.
            </div> */}
          </TextWrapper>

          <StyledButton
            disable={showTestDriveBanner.show}
            css={css`
              width: 82px;
            `}
            onClick={handleVerifyClick}
          >
            Verify
          </StyledButton>
        </SpaceBetween>

        <div className="header grid-row">
          <div />
          <div className="header-title">Type</div>
          <div className="header-title">Hostname</div>
          <div className="header-title">Enter this value</div>
        </div>

        <WrapperList>
          {new Array(2).fill(0).map((_, index) => (
            <>
              <ItemList className="grid-row">
                {isLoading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    {domainInfo.sending_dns_records[index].valid ===
                    Valid.Valid ? (
                      <FaCheckCircle color="#75b85c" />
                    ) : (
                      <FaTimesCircle color="#d02a2a" />
                    )}

                    <div className="row-text">
                      {domainInfo.sending_dns_records[index].record_type || ""}
                    </div>

                    <div className="row-text">
                      {domainInfo.sending_dns_records[index].name || ""}
                    </div>

                    <div className="row-flex">
                      {domainInfo.sending_dns_records[index].value ===
                      "Error" ? (
                        <div className="row-text">
                          {domainInfo.sending_dns_records[index].value || ""}
                        </div>
                      ) : (
                        <InputGroup size="md">
                          <Input
                            value={
                              domainInfo.sending_dns_records[index].value || ""
                            }
                            focusBorderColor="orange.50"
                            pr="4.5rem"
                            type={show ? "text" : "password"}
                            readOnly
                          />
                          <InputRightElement width="4.5rem">
                            <Button
                              className="button"
                              h="1.75rem"
                              size="sm"
                              onClick={handleClick}
                              _focus={{ outline: "none" }}
                            >
                              {show ? "Hide" : "Show"}
                            </Button>
                          </InputRightElement>
                        </InputGroup>
                      )}

                      <div
                        className="icon-button"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            domainInfo.sending_dns_records[index].value || ""
                          );

                          toast("Successfully copied to the clipboard!", {
                            type: "success",
                          });
                        }}
                      >
                        <FaCopy />
                      </div>
                    </div>
                  </>
                )}
              </ItemList>
            </>
          ))}
        </WrapperList>
      </div>

      <div className="section">
        <SpaceBetween>
          <TextWrapper>
            <div className="main-title">Tracking records</div>
            {/* <div className="main-text">
              Lorem ipsum dolor sit, amet consectetur adipisicing elit.
              Blanditiis, sapiente.
            </div> */}
          </TextWrapper>

          <div />
        </SpaceBetween>

        <div className="header grid-row">
          <div />
          <div className="header-title">Type</div>
          <div className="header-title">Hostname</div>
          <div className="header-title">Enter this value</div>
        </div>

        <WrapperList>
          <ItemList className="grid-row">
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                {domainInfo.sending_dns_records[2].valid === Valid.Valid ? (
                  <FaCheckCircle color="#75b85c" />
                ) : (
                  <FaTimesCircle color="#d02a2a" />
                )}
                <div className="row-text">
                  {domainInfo.sending_dns_records[2].record_type || ""}
                </div>
                <div className="row-text">
                  {domainInfo.sending_dns_records[2].name || ""}
                </div>

                <div className="row-flex">
                  {domainInfo.sending_dns_records[2].value === "Error" ? (
                    <div className="row-text">
                      {domainInfo.sending_dns_records[2].value || ""}
                    </div>
                  ) : (
                    <InputGroup size="md">
                      <Input
                        focusBorderColor="orange.50"
                        value={domainInfo.sending_dns_records[2].value || ""}
                        pr="4.5rem"
                        type={show ? "text" : "password"}
                        readOnly
                      />

                      <InputRightElement width="4.5rem">
                        <Button
                          className="button"
                          h="1.75rem"
                          size="sm"
                          _focus={{ outline: "none" }}
                          onClick={handleClick}
                        >
                          {show ? "Hide" : "Show"}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  )}

                  <div
                    className="icon-button"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        domainInfo.sending_dns_records[2].value || ""
                      );

                      toast("Successfully copied to the clipboard!", {
                        type: "success",
                      });
                    }}
                  >
                    <FaCopy />
                  </div>
                </div>
              </>
            )}
          </ItemList>
        </WrapperList>
      </div>
    </Container>
  );
}
